// =============================================================================
// eKYC 3.0 endpoint catalogue.
//
// Two namespaces live in this file during the migration:
//
//   * `stages.*`  — the new stage-organized REST contract documented in
//                   mofsl-ekyc-backend/docs/api/frontend_api_guide.html.
//                   Phase B sagas should consume these.
//
//   * The flat top-level keys (`app`, `registration`, `verification`, `proof`,
//     `esign`, `payment`, `offer`, `congratulation`, `personalInfo`, `logout`,
//     `helpUs`, `nominee`, `feedback`) — the legacy contract from the on-prem
//     backend. Kept in place so unmigrated sagas keep compiling. Removed per
//     stage as Phase B proceeds, fully gone in Phase D.
// =============================================================================

export const endpoints = {
    baseUrl: process.env.REACT_APP_BASE_URL,

    // --- New eKYC 3.0 stage-organized contract (Phase B target) -------------
    // Per docs in mofsl-ekyc-backend/docs/api/frontend_api_guide.html
    stages: {
        // Stage 0 + Stage 11 + cross-stage
        journey: {
            init: '/journey/init',                      // GET, anonymous
            status: '/journey/status',                  // GET, JWT, ?userId=
            stage: '/journey/stage',                    // POST, JWT (analytics)
            finalValidation: '/journey/final-validation', // POST, JWT (Stage 11)
        },

        // Stage 0 — version gate (anonymous)
        app: {
            versionCheck: '/app/version-check',         // POST, anonymous
        },

        // Phase E
        security: {
            publicKey: '/security/public-key',          // GET, anonymous
        },

        // Cross-stage — server-driven option lists (anonymous).
        // GET /lookups/{category} → data: [{ code, displayName }]. Source of
        // truth for the personal-details enum chips (occupation / annual-income /
        // trading-experience) whose codes differ from the legacy hardcoded lists.
        lookups: {
            category: (category) => `/lookups/${encodeURIComponent(category)}`, // GET, anonymous
        },

        // Cross-cutting — token refresh (EW-I6224 / CC-4)
        auth: {
            refresh: '/auth/refresh',                   // POST, anonymous, body { refreshToken }
            exchange: '/auth/exchange',                 // POST, anonymous, body { exchangeCode } → RefreshResult (Zoho RM handoff, TKT-0045)
            resync: '/auth/resync',                     // POST, authenticated (current JWT), body {} → RefreshResult (Stage Completion Lock, 2026-09)
        },

        // Stage 1 + Stage 2
        registration: {
            signup: '/registration/signup',                       // POST, anonymous
            otpGenerate: '/registration/otp/generate',            // POST, anonymous (resend on Stage 1 path)
            otpVerify: '/registration/otp/verify',                // POST, anonymous
            otpResend: '/registration/otp/resend',                // POST, anonymous (Stage 2 cooldown-aware)
            resume: (userId) => `/registration/resume/${encodeURIComponent(userId)}`, // GET, anonymous
            resumeByLead: (leadId) => `/registration/resume/by-lead/${encodeURIComponent(leadId)}`, // GET, anonymous
            resumeByMobile: '/registration/resume/by-mobile',     // GET, anonymous, ?mobile=
            // Stage 4 fast path (TKT-0018): BE runs the Zintlr→Hyperverge→NSDL
            // prefetch + PAN validate/verify server-side (kicked off at OTP).
            // FE polls this after email to decide verified-card vs manual entry.
            prefetchStatus: '/registration/prefetch-status',      // GET, JWT, ?userId=
            // Franchisee registration-URL prefill: a reusable partner link carries
            // ?ekycid=<GUID>; this resolves it to the pre-agreed account-opening
            // params. Anonymous, safe to call repeatedly (pure read).
            details: (ekycId) => `/registration/details/${encodeURIComponent(ekycId)}`, // GET, anonymous
        },

        // Branch quick-link prefill: a branch RM generates a reusable Zoho quick
        // link carrying ?zid=<GUID>; this resolves it to the pre-agreed branch
        // account-opening params. Anonymous, safe to call repeatedly (pure read).
        zoho: {
            quickLinkDetails: (quickLinkId) => `/zoho/quicklinks/details/${encodeURIComponent(quickLinkId)}`, // GET, anonymous
        },

        // Stage 3
        email: {
            current: '/email',                          // GET, JWT, ?leadId=
            otpGenerate: '/email/otp/generate',         // POST, JWT
            otpVerify: '/email/otp/verify',             // POST, JWT
            otpResend: '/email/otp/resend',             // POST, JWT, ?leadId=
        },

        // Stage 4
        pan: {
            details: '/pan/details',                    // GET, JWT, X-Lead-Id header
            // EW-I6380: fetch-only Hyperverge name+DOB for a typed PAN (no NSDL/UTI).
            // Called on PAN entry to populate the fields; always 200 with { found, ... }.
            fetch: '/pan/fetch',                        // POST, JWT
            validate: '/pan/validate',                  // POST, JWT
            verify: '/pan/verify',                      // POST, JWT (prefilled confirm)
            // TKT-0018: vendorless "Proceed" for the server-prefetched PAN —
            // confirms the already-validated PAN (EMAIL_VERIFIED→PAN_VERIFIED),
            // no PAN entry, no vendor calls. Body { userId }.
            confirmPrefill: '/pan/confirm-prefill',     // POST, JWT
            // Staff Branch migration. staffCheck is detection ONLY (never mutates the lead);
            // staffConsent records the agent's YES/NO and, on YES, performs the migration.
            // Neither moves the journey, so after staffConsent the caller resumes whichever
            // movement endpoint it was already going to use (confirmPrefill for the prefetched
            // PAN, validate+verify for a typed one).
            staffCheck: '/pan/staff-check',             // POST, JWT — { userId, panNumber }
            staffConsent: '/pan/staff-consent',         // POST, JWT — { userId, panNumber, consent }
        },

        // Stage 5
        aadhaar: {
            details: '/aadhaar/details',                // GET, JWT, ?leadId=
            digilockerInitiate: '/aadhaar/digilocker/initiate', // POST, JWT
            digilockerCallback: '/aadhaar/digilocker/callback', // POST, JWT
            upload: '/aadhaar/upload',                  // POST, JWT (base64 fallback)
        },

        // Stage 6
        bank: {
            details: '/bank/details',                   // GET, JWT, X-Lead-Id header
            micr: (ifsc) => `/bank/micr/${encodeURIComponent(ifsc)}`,   // GET, JWT
            list: (ifsc) => `/bank/list/${encodeURIComponent(ifsc)}`,   // GET, JWT
            account: '/bank/account',                   // POST, JWT (forward penny drop)
            proofUpload: '/bank/proof-upload',          // POST, JWT (TKT-0030: OCR + name-match + verify in one call)
            reversePennyInitiate: '/bank/reverse-penny/initiate',       // POST, JWT
            reversePennyComplete: '/bank/reverse-penny/complete',       // POST, JWT
        },

        // Stage 8 (selfie/liveness — note backend prefix is /selfie not /liveness)
        liveness: {
            details: '/selfie/details',                       // GET, JWT, X-Lead-Id header
            initiate: '/selfie/liveness/initiate',            // POST, JWT
            callback: '/selfie/liveness/callback',            // POST, JWT
            image:    '/selfie/image',                        // GET, JWT — fetch selfie after callback
        },

        // Stage 8 sub-step — franchisee (application_type PP) "Desired Client Code" page.
        // The lead sits in CLIENT_CODE_PENDING between the selfie callback and Personal
        // Details; both calls are JWT + X-User-Id and answer 400 for every other vertical.
        clientCode: {
            suggested: '/client-code/suggested',              // GET, JWT — idempotent reserve/return of the default
            confirm: '/client-code/confirm',                  // POST, JWT — { clientCode } → lead back to LIVENESS_DONE
        },

        // Stage 8 — assisted-journey "Share via Link" selfie handoff (TKT-0043).
        // A no-camera (desktop) or BRBA/assisted user mints a short URL to finish
        // the selfie on a second device. Replaces legacy POST /proof/shareLink.
        handoff: {
            // TKT-0046: stage-agnostic — the link resumes the lead at its CURRENT
            // stage, so one endpoint serves Stages 5/7/8/10. BE also keeps
            // /handoff/selfie-link as a back-compat alias to the same handler.
            link: '/handoff/link',                                                       // POST, JWT — lead from JWT claim, legacy body accepted+ignored
            resolve: (shortCode) => `/handoff/resolve/${encodeURIComponent(shortCode)}`, // GET, anonymous — 2nd-device resume entry (TKT-0045, not yet wired on FE)
        },

        // Stage 7 (signature)
        signature: {
            details: '/signature/details',              // GET, JWT, X-Lead-Id header
            upload: '/signature/upload',                // POST, JWT
            // Accepts the server-rendered signature preview already produced by
            // GET /signature/details. Body is `{ userId }` ONLY — the client must
            // never submit arbitrary image bytes as an "auto-generated" signature.
            autoGeneratedConfirm: '/signature/auto-generated/confirm', // POST, JWT
            // Kill switch for the whole auto-generated feature, per surface.
            // GET, JWT only (no lead) — `?platform=WEB`. Fail-closed: anything
            // other than an explicit `enabled: true` leaves Stage 7 on the
            // legacy Draw/Upload screen.
            autoGeneratedEnabled: '/signature/auto-generated/enabled',  // GET, JWT
        },

        // Stage 9 (personal details)
        personal: {
            details: '/personal-details',               // GET (X-Lead-Id) / PUT (body), JWT
            dpScheme: '/personal-details/dp-scheme',    // PUT (X-Lead-Id) — CRM DP Scheme dropdown, saved on change
        },

        // Stage 9 (nominees) — RESTful collection
        nominees: {
            list: '/nominees',                          // GET, JWT, X-Lead-Id header
            create: '/nominees',                        // POST, JWT
            update: (id) => `/nominees/${encodeURIComponent(id)}`,  // PUT, JWT
            remove: (id) => `/nominees/${encodeURIComponent(id)}`,  // DELETE, JWT, X-Lead-Id header
            verifyPan: '/nominees/verify-pan',          // POST, JWT
            none: '/nominees/no-nominee',               // POST, JWT, X-Lead-Id header
            optOutDeclaration: '/nominees/opt-out-declaration', // GET, JWT, X-Lead-Id header — popup copy + declared state
            pincodeLookup: '/nominee/getStateCityFromPincode', // GET ?pincode=, JWT — pincode→{city,district,state,stateCode}
        },

        // Stage 10 (conditional, when fnoSelected=true)
        incomeProof: {
            upload: '/income-proof/upload',             // POST, JWT
            submit: '/documents/submit-proofs',         // POST, JWT, X-Lead-Id header
            // OneMoney AA lane (TKT-0055): in-Stage-9 modal flow stays, replaces
            // legacy /proof/getOneMoneyBankDetails + /proof/OneMoneyRedirection.
            aaInitiate: '/income-proof/aa/initiate',    // POST, JWT, X-User-Id header
            aaBankDetails: '/income-proof/aa/bank-details', // GET, JWT, X-User-Id header
            aaCallback: '/income-proof/aa/callback',    // POST, JWT, body { consentId }
        },

        // Stage 12
        documents: {
            generateAof: '/documents/generate-aof',     // POST, JWT
            getPdf: (type) => `/documents/pdf/${encodeURIComponent(type)}`, // GET, JWT, ?leadId=
            regenerate: '/documents/regenerate',                              // POST, JWT
            aofDownload: (userId) => `/documents/aof/download/${encodeURIComponent(userId)}`, // GET, JWT
            ipvGenerate: '/documents/ipv/generate',                           // POST, JWT
            ipvDownload: (userId) => `/documents/ipv/download/${encodeURIComponent(userId)}`, // GET, JWT
        },

        // Stage 13
        esign: {
            details: '/esign/details',                  // GET, JWT, ?leadId=
            status: '/esign/status',                    // GET, JWT — returns redirectUrl when ESIGN_INITIATED
            initiate: '/esign/initiate',                // POST, JWT (returns redirectUrl)
            callback: '/esign/callback',                // POST, JWT
            generateClientCode: '/esign/generate-client-code', // POST, JWT
            mockPage: '/esign/mock-page',               // backend mock UI (dev / mock mode)
        },

        // Stage 14
        account: {
            create: '/account/create',                  // POST, JWT (may return 202 for Non-STP)
            activate: '/account/activate',              // POST, JWT (Stage 15)
            status: '/account/status',                  // GET ?userId=, JWT — read-only minted clientId (TKT-0040)
        },

        // Stage 15
        payment: {
            initiate: '/payment/initiate',              // POST, JWT
            status: '/payment/status',                  // POST, JWT
            callback: '/payment/callback',              // POST, JWT
        },

        // Add Funds — post-account-creation top-up offered on the congratulation
        // screen. BE wraps the legacy 2.0 fund-transfer API and returns a one-shot
        // Epay gateway URL. Distinct from `payment` above (the parked EPay/Razorpay
        // cascade), which this flow does not use.
        addFunds: {
            eligibility: '/add-funds/eligibility',      // GET, JWT
            initiate: '/add-funds/initiate',            // POST, JWT
        },

        // Cross-cutting
        lead: {
            delete: '/lead/delete',                     // POST, JWT (GDPR)
        },

        // Cross-stage — document proof deletion
        proof: {
            deleteProof: '/proof/deleteProof',          // POST, JWT — body { userId, documentType }
        },

        // Rejection Correction module ("Almost there" page) — self-contained
        // post-verifier-rejection correction flow. All routes JWT customer auth
        // (userId from JWT sub). See spec §8 (2026-07-02-rejection-correction-module-design.md).
        rejectionCorrection: {
            status: '/rejection-correction',                              // GET,  JWT — page payload {modules,verifiedCount,total,esignEnabled,draftAofAvailable}
            pep: '/rejection-correction/pep',                             // POST, JWT — { declared: bool } (409 if already VERIFIED)
            fatherSpouse: '/rejection-correction/father-spouse',          // POST, JWT — { relation, name } (BRD_Father_Spouse_Name_Rejection_v1.0)
            bankAccount: '/rejection-correction/bank/account',            // POST, JWT — mirror /bank/account
            bankProofUpload: '/rejection-correction/bank/proof-upload',   // POST, JWT — mirror /bank/proof-upload
            bankReversePennyInitiate: '/rejection-correction/bank/reverse-penny/initiate', // POST, JWT
            bankReversePennyComplete: '/rejection-correction/bank/reverse-penny/complete', // POST, JWT
            signature: '/rejection-correction/signature',                 // POST, JWT — mirror /signature/upload
            esignAck: '/rejection-correction/esign/acknowledge',          // POST, JWT — mark ESIGN module VERIFIED (redo eSign)
            incomeProofDropFno: '/rejection-correction/income-proof/drop-fno', // POST, JWT — strip F&O (equity-only) + mark INCOME_PROOF VERIFIED
            proceedToEsign: '/rejection-correction/proceed-to-esign',     // POST, JWT — gate + PENDING_CORRECTION→DETAILS_DONE
            draftAof: (userId) => `/documents/aof/download/${encodeURIComponent(userId)}`, // GET, JWT — reused pre-warm AOF download
        },

        // Post-eSign F&O onboarding (Stage post-13) — conditional F&O activation
        // offer shown after ESIGN_COMPLETED when journey.postEsignFno is set.
        fno: {
            postEsignDecision: '/fno/post-esign/decision',  // POST, JWT — { decision: "ACTIVATE"|"DEFER", acknowledged: bool }
            aaInitiate: '/fno/post-esign/aa/initiate',      // POST, JWT — { mobile?, returnUrl? } → OneMoney AA redirect
            postEsignStatus: '/fno/post-esign/status',      // GET, JWT — safe repeatable read of F&O screen state
            aaAbandon: '/fno/post-esign/aa/abandon',        // POST, JWT, no body — customer cancelled/backed out of OneMoney
        },

        // Post-account-creation cross-sell layer (card container). Gated by the
        // X-Ekyc-Capabilities: crosssell-v1 request header (services/index.js adds it).
        crossSell: {
            session: '/crosssell/session',                                   // GET, JWT
            shown: (c) => `/crosssell/cards/${encodeURIComponent(c)}/shown`, // POST, JWT
            cta: (c) => `/crosssell/cards/${encodeURIComponent(c)}/cta`,     // POST, JWT
            skip: (c) => `/crosssell/cards/${encodeURIComponent(c)}/skip`,   // POST, JWT
            ret: (c) => `/crosssell/cards/${encodeURIComponent(c)}/return`,  // POST, JWT
            finish: '/crosssell/finish',                                     // POST, JWT
        },

        // SSP (Super Saving Plan) card — BRD v1.2.
        ssp: {
            plans: '/crosssell/ssp/plans',      // GET, JWT — plans from the SSP system, every render
            upgrade: '/crosssell/ssp/upgrade',  // POST, JWT — { schemeId, tncAccepted } → { redirectUrl, … }
            skip: '/crosssell/ssp/skip',        // POST, JWT — { reason: "DEFERRED" } (I'll do it later)
            status: '/crosssell/ssp/status',    // GET, JWT — status + receipt fields
        },
    },

    // --- Legacy on-prem contract (kept until Phase B retires each section) ---
    // baseUrl: process.env.REACT_APP_BASE_URL_JET,
    // baseUrl: process.env.REACT_APP_BASE_URL_SUPERJET,
    // baseUrl: process.env.REACT_APP_BASE_URL_SUPERJET_WEB,
    // baseUrl: "/api/v5",
    // baseUrl: process.env.REACT_APP_BASE_URL_JOINT_HOLDER,

    app: {
        init: '/app/init',
        initSuperApp: '/app/initSuperApp'
        // init: '/app/initDirect'
    },
    user: {
        getUser: '/users'
    },
    registration: {
        getCities: '/registration/bindcity',
        getDetailsFromGuid: '/registration/getDetailsFromGuid',
        openAccount: '/registration/oao',
        validatePartnerCode: '/registration/partnerValidation',
        resumeApplication: '/registration/resumeApplication',
        checkMobileNumberStatus: '/registration/checkMobileNumberStatus',
        getAuRmDetails: '/registration/getAuRmDetails',
        getBaDiyDetails: '/registration/getBaDiyDetails',
        getSchemeDetailsSTPMobile: '/registration/getSchemeDetailsSTPMobile',
        getTruecallerProfile: '/registration/getTruecallerProfile',
        validateReferralCode: '/registration/validateReferralCode',
        loadLeadTypes: '/registration/loadLeadTypes',
        generateRedirectionUrl: '/registration/generateRedirectionUrl',
        subBroker: '/registration/subBroker',
        exportToExcel: '/registration/exportToExcel',
        ekycApproveReject: '/registration/ekycApproveReject',
        getToken: '/proof/getJwtTokenId',
        getAuthForBlogs: '/registration/getAuthForBlogs',
        encryptionForBlogs: '/registration/encryptionForBlogs',
        getBlogsApi: '/registration/getBlogsApi',
        jointAccountHolderOption: '/registration/jointAccountHolderOption',
        getMobile: '/registration/getMobile',
        validateQuickLinkProduct: '/registration/validateQuickLinkProduct',
    },
    verification: {
        otpEmail: '/verification/otpEmail',
        schemeDetails: '/verification/schemeDetails',
        getUserPaymentDetails: '/verification/getUserPaymentDetails',
        resendOtp: '/verification/resendOtp',
        onSchemeChange: '/verification/onSchemeChange',
        saveSchemeDetails: '/verification/saveSchemeDetails',
        insertTraderCode: '/verification/insertTraderCode',
        getTraderCode: '/verification/getTraderCode',
        saveSchemeDetailsDirect: '/verification/saveSchemeDetailsDirect',
    },
    esign: {
        getEsignDetails: '/esign/getEsignDetails',
        getEsignDetailsKra: '/esign/getEsignDetailsKra',
        getEsignDetailsDdpi: '/esign/getEsignDetailsDdpi',
        getAuthMode: '/esign/getAuthMode',
        getAuthModeKra: '/esign/getAuthModeKra',
        getAuthModeDdpi: '/esign/getAuthModeDdpi',
        performDigioEsignRequest: '/esign/performDigioEsignRequest',
        performDigioEsignResponse: '/esign/performDigioEsignResponse',
        performDigioEsignRequestKra: '/esign/performDigioEsignRequestKra',
        performDigioEsignResponseKra: '/esign/performDigioEsignResponseKra',
        getAvailableClientCode: '/esign/getAvailableClientCode',
        generateClientCode: '/esign/generateClientCode',
        pushDataInDrishti: '/esign/pushDataInDrishti',
        downloadPdfForm: '/esign/downloadPdfForm',
        pdfFileDownload: '/esign/pdfFileDownload',
        requestBiometric: '/esign/requestBiometric',
        emudhra: '/esign/emudhra',
        getEmudhra: '/esign/getEmudhra',
        emudhraKra: '/esign/emudhraKra',
        getEmudhraKra: '/esign/getEmudhraKra',
        emudhraDdpi: '/esign/emudhraDdpi',
        getEmudhraDdpi: '/esign/getEmudhraDdpi',
        performEmudhraEsignRequest: '/esign/performEmudhraEsignRequest',
        performEmudhraEsignRequestKra: '/esign/performEmudhraEsignRequestKra',
        performEmudhraEsignRequestDdpi: '/esign/performEmudhraEsignRequestDdpi',
        generateClientCodeMF: '/esign/generateClientCodeMF',
        performHypervergeEsignRequest: '/esign/performHypervergeEsignRequest',
        performHypervergeEsignResp: '/esign/performHypervergeEsignResp',
    },
    proof: {
        uploadProof: '/proof/upload',
        uploadProofUsEquity: '/proof/uploadProofUsEquity',
        getProofList: '/proof/getUploadedProof',
        deleteProof: '/proof/deleteProof',
        deleteProofDetailsUsEquity: '/proof/deleteProofDetailsUsEquity',
        viewProof: '/proof/viewUploadedImage',
        viewUploadedImageUsEquity: '/proof/viewUploadedImageUsEquity',
        getProofVerificationStatus: '/proof/getProofVerificationStatus',
        submitProof: '/proof/submitProofUpload',
        submitOCRProof: '/proof/submitOCRProofUpload',
        captureAdharXML: '/proof/captureAdharXML',
        fetchAdharXML: '/proof/fetchAdharXML',
        checkUncheckMobileAdharLink: '/proof/checkUncheckMobileAdharLink',
        gotoVerificationPageApi: '/proof/gotoVerificationPageApi',
        onSchemeChange: '/verification/onSchemeChange',
        saveSchemeDetails: '/verification/saveSchemeDetails',
        validatePAN: '/proof/validatePAN',
        validateAadhaarApi: '/proof/validateAadhaarApi',
        validateAccountAndIfsc: '/proof/validateAccountAndIfsc',
        validateEmailOTP: '/proof/validateEmailOTP',
        resendEmailOTP: '/proof/resendEmailOTP',
        pdfUpload: '/proof/uploadMultiPart',
        addRemovePOA: '/proof/addRemovePOA',
        uploadMultiPartMobile: '/proof/uploadMultiPartMobile',
        getMICR: '/proof/getMICR',
        deleteNominee: '/nominee/deleteNomineeDetailsById',
        validateNomineePAN: '/proof/validateNomineePAN',
        captureLivePhoto: '/proof/captureLivePhoto',
        saveLivePhoto: '/proof/saveLivePhoto',
        digiLockerRequest: '/proof/digiLockerRequest',
        fairXpayKycRequestRedirectionSuperApp: '/proof/fairXpayKycRequestRedirectionSuperApp',
        digiLockerResponse: '/proof/digiLockerResponse',
        shareLink: '/proof/shareLink',
        validatePanDob: '/proof/validatePanDob',
        verifyPanOnName: '/proof/verifyPanOnName',
        verifyPanNSDL: '/proof/verifyPanNSDL',
        getPanDetails: '/proof/getPanDetails',
        getPANBankDetails: '/proof/getPANBankDetails',
        getCmrCopyDetails: '/proof/getCmrCopyDetails',
        getBankList: '/proof/getBankList',
        getBankListByIfsc: '/proof/getBankListByIfsc',
        getMicrBasedOnBankBranch: '/proof/getMicrBasedOnBankBranch',
        getBankDetails: '/proof/getBankDetails',
        getOneMoneyBankDetails: '/proof/getOneMoneyBankDetails',
        updateMobileNumber: '/proof/updateMobileNumber',
        OneMoneyRedirection: '/proof/OneMoneyRedirection',
        deleteBankDetails: '/proof/deleteBankDetails',
        getAadharDetails: '/proof/getAadhaarDetails',
        getClientPhoto: '/proof/getClientPhoto',
        updateProofStage: '/proof/updateProofStage',
        insertUpdateStageUsEquity: '/proof/insertUpdateStageUsEquity',
        getClientSignature: '/proof/getClientSignature',
        insertNriFlag: '/proof/insertNriFlag',
        validateDpIdApi: '/proof/validateDpIdApi',
        // moveBackwordStatus: '/proof/moveBackwordStatus',
        moveBackwordStatus: '/proof/moveBackwordStatus_direct',
        getAadhaarConsent: '/proof/getAadhaarConsent',
        hypervergeLivePhotoRequest: '/proof/hypervergeLivePhotoRequest',
        ainxtLivePhotoRequest: '/proof/ainxtLivePhotoRequest',
        ainxtLivePhotoResponse: '/proof/ainxtLivePhotoResponse',
        hypervergeLivePhotoResponse: '/proof/hypervergeLivePhotoResponse',
        insertUpdateSmallCasesConsent: '/proof/insertUpdateSmallCasesConsent',
        livePhotoLocationDetails: '/proof/livePhotoLocationDetails',
        getProofDetails: '/proof/getProofDetails',
        getProofDetailsUsEquity: '/proof/getProofDetailsUsEquity',
        getAadhaarDetailSuperApp: '/proof/getAadhaarDetailSuperApp',
        aadhaarLinksUsEquity: '/proof/aadhaarLinksUsEquity',
        ainxtDigiLockerRequest: '/proof/ainxtDigiLockerRequest',
        ainxtDigilockerResponse: '/proof/ainxtDigilockerResponse',
        validatePansign: '/proof/validatePansign',
        updateOneMoneyStatus: '/proof/updateOneMoneyStatus',
        updateMFDFlag: '/proof/updateMFDFlag',
        generateRevPennyToken: '/proof/generateRevPennyToken',
        saveRevPennyDetails: '/proof/saveRevPennyDetails',
        validateBankProofPDF: '/proof/validateBankProofPDF',
        validateBankProofImage: '/proof/validateBankProofImage',
        uploadMultiPartBase64: '/proof/uploadMultiPartBase64',
        getEmail: '/proof/getEmail',
        getSignDetails: '/proof/getSignDetails',
        getIncomeProofDetails: '/proof/getIncomeProofDetails',
        getClientPhototProofDetails: '/proof/getClientPhototProofDetails',
        checkHolderExists: '/proof/checkHolderExists',
        getAadharProofDetails: '/proof/getAadharProofDetails',
        submitOCRProofJoint: '/proof/submitOCRProofJoint',
        validateBankProofPdfBase64: '/proof/validateBankProofPdfBase64',
        diyHelpRequest: '/proof/diyHelpRequest',
        getHolderMobileDetails: '/proof/getHolderMobileDetails',
        changeMaritalStatus: '/proof/changeMaritalStatus',
    },
    payment: {
        init: '/payment/init',
        status: '/payment/status',
        sendChequeApproval: '/payment/sendChequeApproval',
        requestChequeStatus: '/payment/requestChequeStatus',
        skipOrChequePayment: '/payment/skipOrCheque',
        getFundTransferDetails: '/payment/getFundTransferDetails',
        getSchemeDetailsDirect: '/payment/getSchemeDetailsDirect',
        insertUpdateSelectedSegment: '/payment/insertUpdateSelectedSegment'
    },
    offer: {
        getOffersList: '/offer/getOffersList',
        registerWalletOffer: '/offer/registerWalletOffer',
        initiateRedeem: '/offer/initiateRedeem',
        initiateOffer: '/offer/initiateOffer',
        initiateSkip: '/offer/initiateSkip',
        offerpaymentStatus: '/offer/offerpaymentStatus',
        downloadesignPdfForm: '/offer/downloadesignPdfForm',
        getOfferDetails: '/offer/getOfferDetails',
        getValuePackDetails: '/offer/getValuePackDetails',
        initiateValuepack: '/offer/initiateValuepack',
        updateValuePackDetails: '/offer/updateValuePackDetails'
    },
    congratulation: {
        downloadPdfForm: '/congratulation/downloadPdfForm',
        addRefral: '/referral/add',
        validateDirectLogin: '/congratulation/validateDirectLogin'
    },
    personalInfo: {
        getPersonalInfo: '/personalDeatils/get',
        updateDetails: '/personalDeatils/update',
        updatePersonalOtherDetails: '/personalDeatils/updatePersonalOtherDetails',
        getIncomeProofDetails: '/proof/getIncomeProofDetails',
        getPersonalDetailsUsEquity: '/personalDeatils/getPersonalDetailsUsEquity',
        updatePersonalDetailsUsEquity: '/personalDeatils/updatePersonalDetailsUsEquity',
        getFatherSposeDetails: '/personalDeatils/getFatherSposeDetails',
        getHolderTyperDetails: '/personalDeatils/getHolderTyperDetails',

    },
    logout: {
        logout: '/logout'
    },
    helpUs: {
        getWorkDetails: '/helpUs/getWorkDetails',
        updateWorkDetails: '/helpUs/updateWorkDetails'
    },
    nominee: {
        getNomineeRelationshipDropdown: '/nominee/getNomineeRelationshipDropdown',
        getStateCityFromPincode: '/nominee/getStateCityFromPincode',
        bindState: '/nominee/bindState',
        bindCity: '/nominee/bindCity',
        updateNomineeInfo: '/nominee/insertUpdateNomineeDetailsById',
        getNomineeDetailsById: '/nominee/getNomineeDetailsById',
        verifyNomineePanDetails: '/nominee/verifyNomineePanDetails',
    },
    feedback: {
        getFeedback: '/feedback/getFeedback',
        updateFeedbackDetails: '/feedback/updateFeedbackDetails',
        getVideoLinkDetails: '/feedback/getVideoLinkDetails'
    }
}